import React from 'react';

import { cn } from '@/services/utils';

type LabelValuePropsBase = {
    label: string;
    icon?: React.ReactNode;
    className?: string;
    /** Merged onto the value. Use `tw-whitespace-normal` when the value must wrap. */
    valueClassName?: string;
};

/** At least one of `value` or `keySuffix` must be provided. */
export type LabelValueProps = LabelValuePropsBase & (
    | { value: React.ReactNode; keySuffix?: string }
    | { value?: React.ReactNode; keySuffix: string }
);

const hasRenderableContent = (node: React.ReactNode): boolean => {
    if (node === undefined || node === null || node === false) {
        return false;
    }
    if (typeof node === 'string') {
        return node.length > 0;
    }
    return true;
};

const iconWrapperClassName = cn(
    'tw-flex-shrink-0 tw-inline-flex tw-items-center tw-justify-center tw-icon-primary',
    'tw-w-5 tw-h-5 lg:tw-w-6 lg:tw-h-6',
    '[&_svg]:tw-w-full [&_svg]:tw-h-full',
    '[&_img]:tw-w-full [&_img]:tw-h-full [&_img]:tw-object-contain',
);

const LabelValue: React.FC<LabelValueProps> = ({
    label,
    value,
    icon,
    keySuffix,
    className,
    valueClassName,
}) => {
    const showValue = hasRenderableContent(value);
    const showKey = hasRenderableContent(keySuffix);

    if (!showValue && !showKey) {
        throw new Error('LabelValue: at least one of `value` or `keySuffix` must be provided.');
    }

    return (
        <div
            className={cn(
                'tw-inline-flex tw-flex-col tw-items-start tw-gap-1',
                'lg:tw-flex-row lg:tw-items-baseline lg:tw-gap-2',
                className,
            )}
        >
            <span
                title={label}
                className="tw-text-t-xs lg:tw-text-t-sm tw-text-t-secondary tw-whitespace-nowrap"
            >
                {label}
            </span>

            <span className="tw-inline-flex tw-items-center tw-gap-1 lg:tw-contents">
                {icon != null && (
                    <span className={cn(iconWrapperClassName, 'lg:tw-self-center')}>
                        {icon}
                    </span>
                )}

                <span className="tw-inline-flex tw-items-baseline tw-gap-1">
                    {showValue && (
                        <span
                            title={typeof value === 'string' ? value : undefined}
                            className={cn(
                                'tw-text-t-sm lg:tw-text-t-base tw-text-t-primary tw-whitespace-nowrap',
                                valueClassName,
                            )}
                        >
                            {value}
                        </span>
                    )}
                    {showKey && (
                        <span
                            title={keySuffix}
                            className="tw-text-t-xs lg:tw-text-t-sm tw-text-t-primary tw-italic tw-uppercase tw-whitespace-nowrap"
                        >
                            {keySuffix}
                        </span>
                    )}
                </span>
            </span>
        </div>
    );
};

export default LabelValue;
